import { useState } from 'react'

import { Button } from '#/shared/ui/button'
import { Dialog, DialogContent, DialogHeader } from '#/shared/ui/dialog'
import { toast } from '#/shared/ui/sonner'
import { StatusBadge } from '#/shared/ui/status-badge'
import { UnsavedChangesDialog } from '#/shared/ui/unsaved-changes-dialog'

import { passkeyErrorMessage, useNewRecoveryCodes, useRecoveryCodeStatus } from './passkeys'
import { RecoveryCodeList } from './recovery-codes'

function badge(remaining: number): { text: string; variant: 'success' | 'warning' | 'danger' } {
  if (remaining === 0) return { text: 'none left', variant: 'danger' }
  return { text: `${String(remaining)} left`, variant: remaining > 2 ? 'success' : 'warning' }
}

/**
 * The way back in when every passkey is gone.
 *
 * Only the count is shown — the codes themselves are hashed and cannot be read
 * again. Making a new set retires the old one at once, which is also what to
 * do when a printed sheet goes missing.
 */
export function RecoveryCodesCard({ email }: { email: string }) {
  const status = useRecoveryCodeStatus()
  const renew = useNewRecoveryCodes()
  const [confirming, setConfirming] = useState(false)
  const [fresh, setFresh] = useState<string[] | null>(null)
  const remaining = status.data

  return (
    <section className="max-w-3xl rounded-md border border-gray-200 bg-white">
      <header className="flex h-12 items-center justify-between gap-3 rounded-t-md border-b border-gray-200 bg-gray-50 px-4">
        <span className="flex items-center gap-2">
          <h2 className="text-sm font-medium text-gray-900">Recovery codes</h2>
          {remaining === undefined ? null : (
            <StatusBadge variant={badge(remaining).variant}>{badge(remaining).text}</StatusBadge>
          )}
        </span>
        <Button
          size="xs"
          variant="outline"
          className="text-gray-900"
          loading={renew.isPending}
          onClick={() => setConfirming(true)}
        >
          {remaining === 0 ? 'Create codes' : 'Create new codes'}
        </Button>
      </header>
      <p className="text-label px-4 py-3 text-gray-500">
        If you lose every passkey, one code signs you in once so you can add a new passkey. Keep
        them in a password manager or on paper — not on the device your passkey lives on.
      </p>

      <UnsavedChangesDialog
        open={confirming}
        onOpenChange={setConfirming}
        title="Create new recovery codes?"
        description="Your current codes stop working at once. The new ones are shown a single time."
        cancelLabel="Cancel"
        confirmLabel="Create"
        onConfirm={() => {
          setConfirming(false)
          renew.mutate(undefined, {
            onSuccess: setFresh,
            onError: (error) => toast.error(passkeyErrorMessage(error)),
          })
        }}
      />
      <Dialog open={fresh !== null} onOpenChange={(open) => !open && setFresh(null)}>
        <DialogContent className="flex max-w-md flex-col p-0">
          <DialogHeader
            title="Your new recovery codes"
            cancelLabel="Close"
            submitLabel="I saved them"
            onSubmit={() => setFresh(null)}
          />
          <div className="flex flex-col gap-3 p-5">
            <p className="text-label text-gray-500">
              Each works once. Save them now — they are not shown again.
            </p>
            {fresh ? <RecoveryCodeList codes={fresh} email={email} /> : null}
          </div>
        </DialogContent>
      </Dialog>
    </section>
  )
}
